import React, { useState, useEffect } from "react";
import { Plus, Check, ChevronRight, Loader2, Mail, LogOut } from "lucide-react";
import {
  useActiveOrganization,
  useSession,
  useWorkspaceList,
  useUser,
} from "@wacht/tanstack-router";
import { Button } from "@/components/ui/button";
import { CreateWorkspaceForm } from "./create-workspace-form";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export const WorkspaceSelectorMenu = () => {
  const { activeOrganization } = useActiveOrganization();
  const { workspaces: workspacesData, loading: workspacesLoading }:any = useWorkspaceList();
  const { user, loading: userLoading } = useUser();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Extract data array from response if needed, ensure workspaces is always an array
  let workspaces: any[] = [];
  if (workspacesData) {
    if (Array.isArray(workspacesData)) {
      workspaces = workspacesData;
    } else if (workspacesData.data && Array.isArray(workspacesData.data)) {
      workspaces = workspacesData.data;
    }
  }

  // Track when initial load is complete to prevent flash
  useEffect(() => {
    // Reset if loading starts again
    if (workspacesLoading || userLoading || !activeOrganization) {
      setIsInitialLoad(true);
      return;
    }
    
    // Small delay to ensure data is fully processed after loading completes
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [workspacesLoading, userLoading, activeOrganization]);
  const { switchWorkspace, session, signOut } = useSession();
  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      toast.error("Failed to sign out");
    }
  };

  const activeWorkspaceId =
    session?.active_signin?.active_workspace_membership_id;

  // Filter workspaces for the active organization
  // Handle both workspace membership structure and direct workspace structure
  const orgWorkspaces = Array.isArray(workspaces)
    ? workspaces.filter((w: any) => {
        const orgId = w?.organization?.id || w?.workspace?.organization?.id;
        return orgId === activeOrganization?.id;
      })
    : [];

  const getInitials = (name: string) => {
    if (!name) return "W";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getUserName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user?.primary_email_address?.email?.split("@")[0] || "User";
  };

  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return getInitials(`${user.first_name} ${user.last_name}`);
    }
    return user?.primary_email_address?.email?.[0]?.toUpperCase() || "U";
  };

  const handleSwitchWorkspace = async (workspaceId: string) => {
    setIsSwitching(true);
    try {
      await switchWorkspace(workspaceId);
    } catch (error) {
      toast.error("Failed to switch workspace");
    } finally {
      setIsSwitching(false);
    }
  };

  const handleWorkspaceCreated = (workspace?: any) => {
    if (workspace?.id) {
      handleSwitchWorkspace(workspace.id);
    }
    setCreateWorkspaceOpen(false);
  };

  // Show loader during initial load or when data is loading
  if (workspacesLoading || userLoading || isInitialLoad || !activeOrganization) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading workspaces...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 min-h-[500px]">
        {/* Left Section - Header and User Info */}
        <div className="md:col-span-1 bg-muted/30 p-8 flex flex-col justify-between border-r">
          <div className="space-y-6">
            {/* Icon */}
            <div className="w-full h-16 ">
              {/* <Mail className="w-full" /> */}
              <img src="id_full_logo_white.png" className="w-full h-full object-cover rounded-lg" />
            </div>
            
            {/* Title and Subtitle */}
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">Select a workspace</h2>
              <p className="text-sm text-muted-foreground">
                Choose a workspace to get started
              </p>
            </div>
          </div>

          {/* User Profile */}
          <div className="flex items-center gap-3 pt-4 border-t">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.profile_picture_url} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{getUserName()}</p>
              <p className="text-xs text-muted-foreground">Active</p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex-shrink-0 p-1 rounded-md hover:bg-accent transition-colors"
                  aria-label="User menu"
                >
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={handleSignOut}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Right Section - Workspace List */}
        <div className="md:col-span-2 p-8 flex flex-col">
          <div className="mb-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-1">
              AVAILABLE WORKSPACES ({orgWorkspaces.length})
            </h3>
          </div>

          {orgWorkspaces.length > 0 ? (
            <>
              {orgWorkspaces.length > 5 ? (
                <ScrollArea className="flex-1 pr-4" style={{ height: '420px' }}>
                  <div className="space-y-2">
                    {orgWorkspaces.map((workspace: any) => {
                      // Handle workspace membership structure: workspace.id is the membership ID
                      const workspaceMembershipId = workspace.id;
                      const workspaceData = workspace.workspace || workspace;
                      const isActive = activeWorkspaceId === workspaceMembershipId;
                      
                      return (
                        <div
                          key={workspaceMembershipId || workspaceData.id}
                          onClick={() => {
                            if (!isActive && !isSwitching && workspaceMembershipId) {
                              handleSwitchWorkspace(workspaceMembershipId);
                            }
                          }}
                          className={cn(
                            "flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors",
                            isActive
                              ? "bg-primary/10 border-primary"
                              : "bg-background border-border hover:bg-accent hover:border-accent-foreground/20",
                            isSwitching && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          {/* Workspace Icon */}
                          <div className="w-12 h-12 rounded-lg bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center flex-shrink-0">
                            {workspaceData.image_url ? (
                              <img
                                src={workspaceData.image_url}
                                alt={workspaceData.name}
                                className="w-full h-full object-cover rounded-lg"
                              />
                            ) : (
                              getInitials(workspaceData.name || "W")
                            )}
                          </div>

                          {/* Workspace Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-sm font-semibold truncate">
                                {workspaceData.name}
                              </h4>
                              {isActive && (
                                <Check className="w-4 h-4 text-primary flex-shrink-0" />
                              )}
                            </div>
                            {workspaceData.description && (
                              <p className="text-xs text-muted-foreground truncate">
                                {workspaceData.description}
                              </p>
                            )}
                          </div>

                          {/* Arrow Icon */}
                          {!isActive && (
                            <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              ) : (
                <div className="space-y-2 flex-1">
                  {orgWorkspaces.map((workspace: any) => {
                    // Handle workspace membership structure: workspace.id is the membership ID
                    const workspaceMembershipId = workspace.id;
                    const workspaceData = workspace.workspace || workspace;
                    const isActive = activeWorkspaceId === workspaceMembershipId;
                    
                    return (
                      <div
                        key={workspaceMembershipId || workspaceData.id}
                        onClick={() => {
                          if (!isActive && !isSwitching && workspaceMembershipId) {
                            handleSwitchWorkspace(workspaceMembershipId);
                          }
                        }}
                        className={cn(
                          "flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors",
                          isActive
                            ? "bg-primary/10 border-primary"
                            : "bg-background border-border hover:bg-accent hover:border-accent-foreground/20",
                          isSwitching && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        {/* Workspace Icon */}
                        <div className="w-12 h-12 rounded-lg bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center flex-shrink-0">
                          {workspaceData.image_url ? (
                            <img
                              src={workspaceData.image_url}
                              alt={workspaceData.name}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            getInitials(workspaceData.name || "W")
                          )}
                        </div>

                        {/* Workspace Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-sm font-semibold truncate">
                              {workspaceData.name}
                            </h4>
                            {isActive && (
                              <Check className="w-4 h-4 text-primary flex-shrink-0" />
                            )}
                          </div>
                          {workspaceData.description && (
                            <p className="text-xs text-muted-foreground truncate">
                              {workspaceData.description}
                            </p>
                          )}
                        </div>

                        {/* Arrow Icon */}
                        {!isActive && (
                          <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-sm text-muted-foreground">
                No workspaces available
              </p>
            </div>
          )}

          {/* Create Workspace Button */}
          <div className="mt-6 pt-6 border-t">
            <Button
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-12"
              onClick={() => setCreateWorkspaceOpen(true)}
              disabled={isSwitching || !activeOrganization}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create new workspace
            </Button>
          </div>
        </div>
      </div>

      <CreateWorkspaceForm
        organizationId={activeOrganization?.id || ""}
        isOpen={createWorkspaceOpen}
        onClose={() => setCreateWorkspaceOpen(false)}
        onSuccess={handleWorkspaceCreated}
      />
    </>
  );
};

